import type { AgingBucket, TopClientOutstanding, BucketClient, OverallClient } from '../hooks/useOutstandingCollections';

// ─── Formatters ───────────────────────────────────────────────────────────────
export function fmtUSD(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

export function fmtUSDFull(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

// Invoices / balances more than 3×365 days overdue are excluded from UI totals
export const OUTSTANDING_MAX_DAYS_OVERDUE = 3 * 365;

/** Same `aging_bucket` string can appear twice in source data (e.g. two 120+ rows). */
export type AgingBucketRowView = AgingBucket & {
  rowKey: string;
  /** Distinct X-axis / row label when duplicates exist (e.g. "120+ days · Critical"). */
  chartXLabel: string;
};

export function buildAgingBucketRowViews(buckets: AgingBucket[]): AgingBucketRowView[] {
  const counts = new Map<string, number>();
  for (const b of buckets) {
    counts.set(b.aging_bucket, (counts.get(b.aging_bucket) || 0) + 1);
  }
  return buckets.map((b, i) => {
    const isDup = (counts.get(b.aging_bucket) || 0) > 1;
    return {
      ...b,
      rowKey: `${b.aging_bucket}|${String(b.collection_risk)}|${i}`,
      chartXLabel: isDup
        ? `${b.bucket_display_name} · ${b.collection_risk}`
        : b.bucket_display_name,
    };
  });
}

// ─── Safe JSON parse for clients_list ────────────────────────────────────────
export function parseBucketClientsList(raw: string): BucketClient[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((c: Record<string, unknown>) => ({
      name: String(c.name ?? ''),
      outstanding: Number(c.outstanding) || 0,
      tier: String(c.tier ?? ''),
      status: String(c.status ?? ''),
      invoice_count: Number(c.invoice_count) || 0,
      days_overdue: Number(c.days_overdue) || 0,
      paid_12m_count: Number(c.paid_12m_count) || 0,
      paid_12m_amount: Number(c.paid_12m_amount) || 0,
      last_invoice: String(c.last_invoice ?? ''),
    }));
  } catch {
    return [];
  }
}

export function parseBucketClients(raw: string): BucketClient[] {
  return parseBucketClientsList(raw).filter(
    c => (c.days_overdue ?? 0) <= OUTSTANDING_MAX_DAYS_OVERDUE
  );
}

export function parseOverallClients(raw: string | undefined): OverallClient[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (c: OverallClient) => (c.max_days_overdue ?? 0) <= OUTSTANDING_MAX_DAYS_OVERDUE
    );
  } catch {
    return [];
  }
}

function filterTopClientsOverallListJson(raw: string | undefined, maxDays: number): string | undefined {
  if (!raw) return raw;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return raw;
    const out = arr.filter((c: { max_days_overdue?: number }) =>
      (Number(c.max_days_overdue) || 0) <= maxDays
    );
    return JSON.stringify(out);
  } catch {
    return raw;
  }
}

function sumTier(clients: BucketClient[], tier: string): number {
  const t = tier.toLowerCase();
  return clients.reduce((s, c) => {
    if (String(c.tier || '').toLowerCase() !== t) return s;
    return s + c.outstanding;
  }, 0);
}

function adjustOneBucket(bucket: AgingBucket, maxDays: number): AgingBucket | null {
  const rawClients = parseBucketClientsList(bucket.clients_list ?? '');
  if (rawClients.length === 0) {
    if (Number(bucket.min_days_overdue) > maxDays) {
      return {
        ...bucket,
        total_outstanding: 0,
        invoice_count: 0,
        unique_clients: 0,
        clients_list: '[]',
        enterprise_outstanding: 0,
        strategic_outstanding: 0,
        standard_outstanding: 0,
        small_outstanding: 0,
        percentage_of_total: 0,
        avg_outstanding: 0,
        min_days_overdue: 0,
        max_days_overdue: 0,
        avg_days_overdue: 0,
      };
    }
    return { ...bucket };
  }

  const clients = rawClients.filter(c => (c.days_overdue ?? 0) <= maxDays);
  if (clients.length === 0) return null;

  const total_outstanding = clients.reduce((s, c) => s + c.outstanding, 0);
  const invoice_count = clients.reduce((s, c) => s + c.invoice_count, 0);
  const unique_clients = clients.length;
  const daysVals = clients.map(c => c.days_overdue);
  const min_days_overdue = Math.min(...daysVals);
  const max_days_overdue = Math.max(...daysVals);
  const weightSum = clients.reduce((s, c) => s + c.outstanding, 0);
  const avg_days_overdue =
    weightSum > 0
      ? clients.reduce((s, c) => s + c.days_overdue * c.outstanding, 0) / weightSum
      : clients.reduce((s, c) => s + c.days_overdue, 0) / clients.length;

  return {
    ...bucket,
    total_outstanding,
    invoice_count,
    unique_clients,
    min_days_overdue,
    max_days_overdue,
    avg_days_overdue,
    avg_outstanding: invoice_count > 0 ? total_outstanding / invoice_count : 0,
    enterprise_outstanding: sumTier(clients, 'enterprise'),
    strategic_outstanding: sumTier(clients, 'strategic'),
    standard_outstanding: sumTier(clients, 'standard'),
    small_outstanding: sumTier(clients, 'small'),
    clients_list: JSON.stringify(clients),
  };
}

function applyOrgLevelFieldsToAgingBuckets(buckets: AgingBucket[]): AgingBucket[] {
  if (buckets.length === 0) return [];

  const grandTotal = buckets.reduce((s, b) => s + Number(b.total_outstanding), 0);
  const totalInvoices = buckets.reduce((s, b) => s + Number(b.invoice_count), 0);

  const sumRisk = (risk: string) =>
    buckets
      .filter(
        b => String(b.collection_risk || '').trim().toLowerCase() === risk.toLowerCase()
      )
      .reduce((s, b) => s + Number(b.total_outstanding), 0);

  const critical_outstanding = sumRisk('critical');
  const high_risk_outstanding = sumRisk('high');
  const medium_risk_outstanding = sumRisk('medium');
  const low_risk_outstanding = sumRisk('low');

  const allNames = new Set<string>();
  for (const b of buckets) {
    parseBucketClientsList(b.clients_list).forEach(c => {
      const n = c.name.trim();
      if (n) allNames.add(n);
    });
  }

  const overallListFiltered = filterTopClientsOverallListJson(
    buckets[0]?.top_clients_overall_list,
    OUTSTANDING_MAX_DAYS_OVERDUE
  );

  const currentOutstandingTotal = buckets
    .filter(x => x.avg_days_overdue < 0)
    .reduce((s, x) => s + Number(x.total_outstanding), 0);
  const overdueOutstandingTotal = grandTotal - currentOutstandingTotal;

  return buckets.map(b => ({
    ...b,
    grand_total_outstanding: grandTotal,
    total_invoices: totalInvoices,
    total_unique_clients: allNames.size,
    percentage_of_total: grandTotal > 0 ? (Number(b.total_outstanding) / grandTotal) * 100 : 0,
    critical_outstanding,
    high_risk_outstanding,
    medium_risk_outstanding,
    low_risk_outstanding,
    overdue_outstanding: overdueOutstandingTotal,
    current_outstanding: currentOutstandingTotal,
    overall_overdue_pct:
      grandTotal > 0 ? (overdueOutstandingTotal / grandTotal) * 100 : 0,
    critical_risk_pct: grandTotal > 0 ? (critical_outstanding / grandTotal) * 100 : 0,
    top_clients_overall_list: overallListFiltered ?? b.top_clients_overall_list,
  }));
}

/** Drop invoice-level amounts beyond 3 years overdue; rebucket KPIs and % of total. */
export function adjustAgingBucketsForMaxDaysOverdue(buckets: AgingBucket[]): AgingBucket[] {
  const maxDays = OUTSTANDING_MAX_DAYS_OVERDUE;
  const adjusted = buckets
    .map(b => adjustOneBucket(b, maxDays))
    .filter((b): b is AgingBucket => b != null);

  if (adjusted.length === 0) return [];

  return applyOrgLevelFieldsToAgingBuckets(adjusted);
}

function mergeBucketClientsByName(rows: BucketClient[]): BucketClient[] {
  const m = new Map<string, BucketClient>();
  for (const c of rows) {
    const k = c.name.trim().toLowerCase();
    const prev = m.get(k);
    if (!prev) {
      m.set(k, { ...c, name: c.name.trim() });
    } else {
      m.set(k, {
        ...prev,
        outstanding: prev.outstanding + c.outstanding,
        invoice_count: prev.invoice_count + c.invoice_count,
        days_overdue: Math.max(prev.days_overdue, c.days_overdue),
        paid_12m_count: prev.paid_12m_count + c.paid_12m_count,
        paid_12m_amount: prev.paid_12m_amount + c.paid_12m_amount,
        last_invoice:
          String(c.last_invoice || '') > String(prev.last_invoice || '')
            ? c.last_invoice
            : prev.last_invoice,
      });
    }
  }
  return Array.from(m.values());
}

function worstCollectionRisk(risks: string[]): string {
  let score = -1;
  let label = 'High';
  const tiers: Array<{ test: (s: string) => boolean; name: string; sc: number }> = [
    { test: s => s.includes('critical'), name: 'Critical', sc: 4 },
    { test: s => s.includes('high'), name: 'High', sc: 3 },
    { test: s => s.includes('medium'), name: 'Medium', sc: 2 },
    { test: s => s.includes('low'), name: 'Low', sc: 1 },
  ];
  for (const r of risks) {
    const s = String(r || '').toLowerCase();
    for (const t of tiers) {
      if (t.test(s) && t.sc > score) {
        score = t.sc;
        label = t.name;
      }
    }
  }
  return label;
}

function isMergeIntoNinetyPlus(b: AgingBucket): boolean {
  const a = (b.aging_bucket || '').trim().toLowerCase();
  return a === '91-120 days' || a.includes('120+');
}

/**
 * Combine 91–120 days and all 120+ rows into one "90+ days" bucket (chart + table).
 */
export function mergeNinetyPlusAgingBuckets(buckets: AgingBucket[]): AgingBucket[] {
  const kept: AgingBucket[] = [];
  const mergeGroup: AgingBucket[] = [];
  for (const b of buckets) {
    if (isMergeIntoNinetyPlus(b)) mergeGroup.push(b);
    else kept.push(b);
  }
  if (mergeGroup.length === 0) return buckets;

  const template = mergeGroup[0];
  const allClients: BucketClient[] = [];
  for (const b of mergeGroup) {
    allClients.push(...parseBucketClientsList(b.clients_list));
  }
  const mergedClients = mergeBucketClientsByName(allClients);

  const total_outstanding = mergeGroup.reduce((s, b) => s + Number(b.total_outstanding), 0);
  const invoice_count = mergeGroup.reduce((s, b) => s + Number(b.invoice_count), 0);
  const weight = mergeGroup.reduce((s, b) => s + Number(b.total_outstanding), 0);
  const avg_days_overdue =
    weight > 0
      ? mergeGroup.reduce(
          (s, b) => s + Number(b.avg_days_overdue) * Number(b.total_outstanding),
          0
        ) / weight
      : mergeGroup.reduce((s, b) => s + Number(b.avg_days_overdue), 0) / mergeGroup.length;

  const collection_risk = worstCollectionRisk(mergeGroup.map(b => b.collection_risk));
  const action_priority =
    mergeGroup.map(b => b.action_priority).find(p => String(p).includes('IMMEDIATE')) ||
    mergeGroup.map(b => b.action_priority).find(p => String(p).includes('HIGH')) ||
    template.action_priority;

  const insertAt = (() => {
    const i = kept.findIndex(b => String(b.aging_bucket).toLowerCase().includes('61-90'));
    return i >= 0 ? i + 1 : kept.length;
  })();

  const merged: AgingBucket = {
    ...template,
    aging_bucket: '90+ days',
    bucket_display_name: '90+ days',
    bucket_description: 'Invoices overdue more than 90 days',
    date_duration: template.date_duration,
    collection_risk,
    action_priority,
    invoice_count,
    unique_clients: mergedClients.length,
    total_outstanding,
    avg_outstanding: invoice_count > 0 ? total_outstanding / invoice_count : 0,
    min_days_overdue: Math.min(...mergeGroup.map(b => Number(b.min_days_overdue) || 0)),
    max_days_overdue: Math.max(...mergeGroup.map(b => Number(b.max_days_overdue) || 0)),
    avg_days_overdue,
    enterprise_outstanding: mergeGroup.reduce((s, b) => s + Number(b.enterprise_outstanding), 0),
    strategic_outstanding: mergeGroup.reduce((s, b) => s + Number(b.strategic_outstanding), 0),
    standard_outstanding: mergeGroup.reduce((s, b) => s + Number(b.standard_outstanding), 0),
    small_outstanding: mergeGroup.reduce((s, b) => s + Number(b.small_outstanding), 0),
    active_clients: mergeGroup.reduce((s, b) => s + Number(b.active_clients ?? 0), 0),
    inactive_clients: mergeGroup.reduce((s, b) => s + Number(b.inactive_clients ?? 0), 0),
    total_original_amount: mergeGroup.reduce((s, b) => s + Number(b.total_original_amount), 0),
    total_paid_amount: mergeGroup.reduce((s, b) => s + Number(b.total_paid_amount), 0),
    avg_priority_score:
      mergeGroup.reduce((s, b) => s + Number(b.avg_priority_score), 0) / mergeGroup.length,
    clients_list: JSON.stringify(mergedClients),
  };

  const result = [...kept.slice(0, insertAt), merged, ...kept.slice(insertAt)];
  return applyOrgLevelFieldsToAgingBuckets(result);
}

// ─── Summary from first bucket ────────────────────────────────────────────────
export interface AgingSummary {
  grandTotal: number;
  totalInvoices: number;
  totalClients: number;
  criticalOutstanding: number;
  overdueOutstanding: number;
  currentOutstanding: number;
  overduePct: number;
  criticalRiskPct: number;
  collectionsAlert: string;
  isUrgent: boolean;
}

export function extractSummary(buckets: AgingBucket[]): AgingSummary {
  const first = buckets[0];
  if (!first) {
    return {
      grandTotal: 0, totalInvoices: 0, totalClients: 0,
      criticalOutstanding: 0, overdueOutstanding: 0, currentOutstanding: 0,
      overduePct: 0, criticalRiskPct: 0, collectionsAlert: '', isUrgent: false,
    };
  }
  const grandTotal = buckets.reduce((s, b) => s + Number(b.total_outstanding), 0);
  const totalInvoices = buckets.reduce((s, b) => s + Number(b.invoice_count), 0);
  const currentOutstanding = buckets
    .filter(b => b.avg_days_overdue < 0)
    .reduce((s, b) => s + Number(b.total_outstanding), 0);
  const criticalOutstanding = buckets
    .filter(b => String(b.collection_risk || '').toLowerCase() === 'critical')
    .reduce((s, b) => s + Number(b.total_outstanding), 0);
  const overdueOutstanding = grandTotal - currentOutstanding;
  return {
    grandTotal,
    totalInvoices,
    totalClients: first.total_unique_clients,
    criticalOutstanding,
    overdueOutstanding,
    currentOutstanding,
    overduePct: grandTotal > 0 ? (overdueOutstanding / grandTotal) * 100 : 0,
    criticalRiskPct: grandTotal > 0 ? (criticalOutstanding / grandTotal) * 100 : 0,
    collectionsAlert: first.collections_alert ?? '',
    isUrgent: first.collections_alert === 'URGENT_COLLECTIONS',
  };
}

// ─── Risk color helpers ───────────────────────────────────────────────────────
export function riskColor(risk: string): string {
  const r = risk.toLowerCase();
  if (r.includes('critical') || r.includes('no risk') === false && r.includes('120')) return '#EF4444';
  if (r.includes('high')) return '#F97316';
  if (r.includes('medium')) return '#F59E0B';
  if (r.includes('low')) return '#10B981';
  if (r.includes('no risk') || r === 'current') return '#3B82F6';
  return '#64748B';
}

export function bucketBarColor(bucket: AgingBucket): string {
  const risk = String(bucket.collection_risk || '').toLowerCase();
  const aging = String(bucket.aging_bucket || '');
  if (aging.includes('90+')) {
    if (risk.includes('critical')) return '#EF4444';
    if (risk.includes('high')) return '#F97316';
    if (risk.includes('medium')) return '#F59E0B';
    if (risk.includes('low')) return '#10B981';
    return '#EF4444';
  }
  // Legacy rows (pre-merge): two "120+ days" — color by risk
  if (aging.includes('120')) {
    if (risk.includes('critical')) return '#EF4444';
    if (risk.includes('high')) return '#F97316';
    if (risk.includes('medium')) return '#F59E0B';
    if (risk.includes('low')) return '#10B981';
    return '#EF4444';
  }
  if (risk.includes('critical')) return '#EF4444';
  if (risk.includes('high')) return '#F97316';
  if (risk.includes('medium') || aging.includes('61')) return '#F59E0B';
  if (risk.includes('low') || aging.includes('31')) return '#10B981';
  return '#3B82F6'; // Current
}

export function riskBadgeClass(category: string): string {
  const c = category.toLowerCase();
  if (c.includes('critical')) return 'bg-red-50 text-red-700 border-red-200';
  if (c.includes('high')) return 'bg-orange-50 text-orange-700 border-orange-200';
  if (c.includes('medium')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (c.includes('low')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

export function tierBadgeClass(tier: string): string {
  const t = tier.toLowerCase();
  if (t === 'enterprise') return 'bg-violet-50 text-violet-700 border-violet-200';
  if (t === 'strategic') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (t === 'standard') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-slate-50 text-slate-500 border-slate-200';
}

// ─── Top Clients filters ──────────────────────────────────────────────────────
export type OutstandingFilter = 'all' | 'critical_risk' | 'high_priority' | 'overdue_90_plus' | 'overdue_31_90' | 'overdue_1_30';
export type SortBy = 'outstanding' | 'days_overdue';

export function applyTopClientsFilter(
  clients: TopClientOutstanding[],
  filter: OutstandingFilter,
  sortBy: SortBy
): TopClientOutstanding[] {
  let filtered = [...clients];

  switch (filter) {
    case 'critical_risk':
      filtered = filtered.filter(c => c.collection_risk_category === 'Critical_Risk');
      break;
    case 'high_priority':
      filtered = filtered.filter(c => c.collection_priority_score > 1000);
      break;
    case 'overdue_90_plus':
      filtered = filtered.filter(c => c.outstanding_90_plus > 0);
      break;
    case 'overdue_31_90':
      filtered = filtered.filter(c => c.outstanding_31_90 > 0);
      break;
    case 'overdue_1_30':
      filtered = filtered.filter(c => c.outstanding_1_30 > 0);
      break;
  }

  if (sortBy === 'days_overdue') {
    filtered = filtered.filter(c => c.avg_days_overdue > 0);
    filtered.sort((a, b) => b.avg_days_overdue - a.avg_days_overdue);
  } else {
    filtered.sort((a, b) => b.total_outstanding - a.total_outstanding);
  }

  return filtered;
}
